import { useEffect, useState } from "react";
import useReportStore from "../../store/useReportStore";
import useAuthStore from "../../store/useAuthStore";
import useProfileStore from "../../store/useProfileStore";
import useCoursesStore from "../../store/useCoursesStore";

export const useReportFlow = () => {
  const {
    fetchReport,
    fetchReportStatus,
    fetchReportTranslateStatus,
    fetchReportFeedback,
    report,
    reportId,
    fetchAllReports,
    reportProcessing,
  } = useReportStore();
  const { access_token, isRefreshing, cvProcessing } = useAuthStore();
  const { fetchRecommendedCourses } = useCoursesStore();
  const { profileLanguage } = useProfileStore();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [translationProgress, setTranslationProgress] = useState(0);

  const currentLanguage = Object.keys(profileLanguage ?? {})[0] ?? "en";
  const isEnglish = currentLanguage === "en";

  // ------------------ Main controller ------------------
  useEffect(() => {
    if (
      !access_token ||
      !reportId ||
      isRefreshing ||
      reportProcessing !== "processing"
    ) {
      return;
    }

    let statusInterval: any = null;

    const monitorReportStatus = async () => {
      try {
        const statusResp = await fetchReportStatus(reportId);

        if (statusResp.data.status === "completed") {
          clearInterval(statusInterval);
          setIsProcessing(false);
        } else {
          fetchAllReports(currentLanguage);
        }
      } catch (err) {
        console.error("Error fetching report status", err);
      }
    };

    const startFlow = async () => {
      setIsProcessing(true);
      try {
        const statusResp = await fetchReportStatus(reportId);

        if (statusResp.data.status === "completed") {
          setIsProcessing(false);
        } else {
          statusInterval = setInterval(monitorReportStatus, 3000);
        }
      } catch (err) {
        console.error("Error starting report flow", err);
        setIsProcessing(false);
      }
    };

    startFlow();

    return () => {
      clearInterval(statusInterval);
    };
  }, [access_token, reportId, currentLanguage, reportProcessing]);

  useEffect(() => {
    if (cvProcessing !== "processing" && reportProcessing !== "processing") {
      if (reportId !== null && reportId !== undefined) {
        fetchReport(reportId, currentLanguage);
        if (isTranslating) setIsTranslating(false);
        return;
      }
    }
  }, [reportProcessing, reportId, currentLanguage]);

  useEffect(() => {
    if (!reportId || reportProcessing === "processing" || isEnglish) {
      return;
    }

    let translationInterval: any = null;
    let cancelled = false;

    const retryIfNotReady = async (
      fn: () => Promise<any>,
      maxRetries = 5,
      delayMs = 500
    ): Promise<any> => {
      for (let attempt = 0; attempt < maxRetries; attempt++) {
        try {
          return await fn();
        } catch (err: any) {
          console.log("THIS IS ERROR", err);

          if (err?.response?.status === 404 && attempt < maxRetries - 1) {
            await new Promise((res) => setTimeout(res, delayMs));
          } else {
            throw err;
          }
        }
      }
    };

    const checkTranslation = async () => {
      try {
        const translationResp = await retryIfNotReady(() =>
          fetchReportTranslateStatus(currentLanguage, reportId)
        );

        if (cancelled) return;

        if (translationResp.data.status === "completed") {
          clearInterval(translationInterval);
          await fetchReport(reportId, currentLanguage);
          setIsTranslating(false);
          setIsProcessing(false);
        } else {
          setIsTranslating(true);
          setTranslationProgress(translationResp.data.progress ?? 0);
          fetchAllReports(currentLanguage);
        }
      } catch (err: any) {
        console.error("Error in delayed translation check", err);
        clearInterval(translationInterval);

        if (err?.response?.status !== 404) {
          try {
            await fetchReport(reportId, currentLanguage);
          } catch (e) {
            console.error("Failed to load current report after error", e);
          }
        }
      }
    };

    checkTranslation();
    translationInterval = setInterval(checkTranslation, 3000);

    return () => {
      clearInterval(translationInterval);
      cancelled = true;
    };
  }, [currentLanguage, reportId, reportProcessing, isEnglish]);

  useEffect(() => {
    if (report && !isProcessing && reportId) {
      fetchReportFeedback(reportId);
      fetchRecommendedCourses();
    }
  }, [report, isProcessing, reportProcessing]);

  return {
    isProcessing,
    isTranslating,
    translationProgress,
  };
};
